import React from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { Grid, Modal, Typography, TextField, FormControlLabel, Checkbox, Autocomplete, Chip, Stack, Select, MenuItem } from "@mui/material";

import { Button, Alert } from "../../../common/styled";
import ModalBox from "../../../../common/ModalBox";

import useLLMConfig from "../../../../../hooks/user/useLLMConfig";

const MatchTypes = ['search', 'fullmatch'];

function RegexModal({ open, handleClose, zoneUid }) {
  const { llmPromptScanners, configLLMSettingPromptScanners } = useLLMConfig();

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        patterns: llmPromptScanners ? llmPromptScanners?.regex?.patterns : [],
        match_type: llmPromptScanners ? llmPromptScanners?.regex?.match_type : "",
        is_blocked: llmPromptScanners ? llmPromptScanners?.regex?.is_blocked : false,
        redact: llmPromptScanners ? llmPromptScanners?.regex?.redact : false,
      }}
      validationSchema={Yup.object().shape({
        patterns: Yup.array().of(Yup.string()),
        match_type: Yup.string().nullable().required("Match type is required"),
        is_blocked: Yup.bool(),
        redact: Yup.bool(),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          values.submit = undefined;
          await configLLMSettingPromptScanners(zoneUid, "regex", values);
          handleClose();
          resetForm();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, isSubmitting, touched, values }) => (
        <React.Fragment>
          <Modal
            open={open}
            onClose={(_, reason) => {
              if (reason !== "backdropClick") {
                handleClose();
              }
            }}
          >
            <ModalBox sx={{ width: "60vw" }} title="Edit Regex Scanner" handleClose={handleClose}>
              <form noValidate onSubmit={handleSubmit}>
                <Grid container p={4} spacing={3}>
                  <Grid item xs={12}>
                    {errors.submit && (
                      <Alert mt={2} mb={3} variant="outlined" severity="error">
                        {errors.submit}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4} mt={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_blocked"
                          checked={values.is_blocked}
                          error={Boolean(touched.is_blocked && errors.is_blocked)}
                          helperText={touched.is_blocked && errors.is_blocked}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label="Is Blocked"
                    />
                  </Grid>
                  <Grid item xs={12} md={8} mt={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="redact"
                          checked={values.redact}
                          error={Boolean(touched.redact && errors.redact)}
                          helperText={touched.redact && errors.redact}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label="Redact"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Match Type</Typography>
                      <Select
                        fullWidth
                        name="match_type"
                        value={values.match_type}
                        error={Boolean(touched.match_type && errors.match_type)}
                        helperText={touched.match_type && errors.match_type}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      >
                        {MatchTypes.map((type) => (
                          <MenuItem value={type} key={`match_type_${type}`}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Patterns</Typography>
                      <Autocomplete
                        fullWidth
                        multiple
                        freeSolo
                        name="patterns"
                        options={[]}
                        value={values.patterns}
                        error={Boolean(touched.patterns && errors.patterns)}
                        helperText={touched.patterns && errors.patterns}
                        onChange={(event, newValue) => setFieldValue("patterns", newValue)}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => <Chip variant="filled" color="warning" label={option} {...getTagProps({ index })} />)
                        }
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Type and press Enter" />}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} textAlign={"right"} pt={4}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
                      <Button variant="outlined" color="secondary" size="modal" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color="success" size="modal" disabled={isSubmitting}>
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </ModalBox>
          </Modal>
        </React.Fragment>
      )}
    </Formik>
  );
}

export default RegexModal;
